<template>
  <div v-if="visible" class="p-dialog-wrapper" @click="closeableOutside ? closeOnOutsideClick : null">
    <div class="p-dialog" :style="dialogStyle" :class="{ 'content-only': hideHeader }" @click.stop>
      <div class="p-dialog-header" v-if="!hideHeader">
        <h5 class="p-dialog-title">{{ header || title }}</h5>
        <button class="p-dialog-close" @click="close">×</button>
      </div>
      <div class="p-dialog-content" :class="{ 'full-height': hideHeader, 'transparent-content': transparentBackground }">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @component CustomDialog
 * @description Componente de diálogo modal personalizado.
 * Permite mostrar información o formularios en una ventana modal con fondo oscurecido.
 */
export default {
  name: 'CustomDialog',
  props: {
    /**
     * Determina si el diálogo es visible
     */
    visible: {
      type: Boolean,
      default: false
    },
    /**
     * Título que se muestra en el encabezado del diálogo
     */
    header: {
      type: String,
      default: ''
    },
    /**
     * Alias para header (compatibilidad)
     */
    title: {
      type: String,
      default: ''
    },
    breakpoints: {
      type: Object,
      default: () => ({})
    },
    style: {
      type: Object,
      default: () => ({})
    },
    maximizable: {
      type: Boolean,
      default: false
    },
    modal: {
      type: Boolean,
      default: true
    },
    /**
     * Oculta el encabezado del diálogo
     */
    hideHeader: {
      type: Boolean,
      default: false
    },
    /**
     * Hace que el fondo del diálogo sea transparente
     */
    transparentBackground: {
      type: Boolean,
      default: false
    },
    /**
     * Determina si el diálogo debe cerrarse al hacer clic fuera de él
     */
    closeableOutside: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    dialogStyle() {
      return {
        ...this.style
      };
    }
  },
  methods: {
    /**
     * Cierra el diálogo emitiendo el evento 'update:visible'
     */
    close() {
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    /**
     * Cierra el diálogo al hacer clic fuera de él
     */
    closeOnOutsideClick() {
      this.close();
    }
  },
  emits: ['update:visible', 'close']
};
</script>

<style scoped>
.p-dialog-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1000;

  backdrop-filter: blur(3px);
}

.p-dialog-wrapper.transparent-bg {
  background-color: transparent;
}

.p-dialog {
  display: flex;
  background-color: var(--surface-0);
  border-radius: var(--border-radius);
  -webkit-box-shadow: var(--shadow-lg);
  box-shadow: var(--shadow-lg);
  width: 80%;
  height: 90%;
  flex-direction: column;
}

.p-dialog.content-only {
  height: 100%;
}

.p-dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid var(--surface-200);
  background-color: var(--surface-50);
  color: var(--text-color);
  height: 5%;
}

.p-dialog-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
}

.p-dialog-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-color);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  transition: background-color var(--transition-fast);
  padding: 0;
}

.p-dialog-close:hover {
  background-color: var(--surface-200);
}

.p-dialog-content {
  padding: 1rem;
  color: var(--text-color);
  height: 95%;
  background-color: var(--surface-0);
  overflow-y: auto;
  /* Para permitir scroll si el contenido es muy grande */
}

.p-dialog-content.full-height {
  height: 100%;
}

.transparent-bg {
  background-color: transparent;
  backdrop-filter: none;
}

.content-only {
  background-color: transparent;
  box-shadow: none;
}

.full-height {
  height: 100%;
}

.p-dialog-content.transparent-content {
  background-color: transparent;
}

@media (max-width: 768px) {
  .p-dialog {
    width: 100%;
    height: 100%;
    max-height: none;
  }

  .p-dialog-header {
    padding: 0.75rem;
  }

  .p-dialog-content {
    padding: 0.75rem;
  }
}
</style>
